export const TEMP_USER_MESSAGE_ID = -1
export const TEMP_ASSISTANT_MESSAGE_ID = -2
export const SYSTEM_MESSAGE_ID = -3
export const SEARCH_TOOL_NAME = 'search'

export enum RetrievalType {
  None = 'none',
  Search = 'search',
  SelectedDocs = 'selectedDocs',
}

export enum ValidSources {
  Web = 'web',
  GitHub = 'github',
  GitLab = 'gitlab',
  Slack = 'slack',
  GoogleDrive = 'google_drive',
  Gmail = 'gmail',
  Bookstack = 'bookstack',
  Confluence = 'confluence',
  Jira = 'jira',
  Productboard = 'productboard',
  Slab = 'slab',
  Notion = 'notion',
  Guru = 'guru',
  Gong = 'gong',
  Zulip = 'zulip',
  Linear = 'linear',
  Hubspot = 'hubspot',
  Document360 = 'document360',
  File = 'file',
  GoogleSites = 'google_sites',
  Loopio = 'loopio',
  Dropbox = 'dropbox',
  Discord = 'discord',
  Salesforce = 'salesforce',
  Sharepoint = 'sharepoint',
  Teams = 'teams',
  Zendesk = 'zendesk',
  Discourse = 'discourse',
  Axero = 'axero',
  Clickup = 'clickup',
  Wikipedia = 'wikipedia',
  Mediawiki = 'mediawiki',
  Asana = 'asana',
  S3 = 's3',
  R2 = 'r2',
  GoogleCloudStorage = 'google_cloud_storage',
  Xenforo = 'xenforo',
  OciStorage = 'oci_storage',
  NotApplicable = 'not_applicable',
  IngestionApi = 'ingestion_api',
  Freshdesk = 'freshdesk',
  Fireflies = 'fireflies',
  Egnyte = 'egnyte',
  Airtable = 'airtable',
  Gitbook = 'gitbook',
}

export interface OnyxDocument {
  document_id: string
  link: string
  source_type: ValidSources
  blurb: string
  semantic_identifier: string | null
  boost: number
  hidden: boolean
  score: number
  chunk_ind: number
  match_highlights: string[]
  metadata: { [key: string]: string }
  updated_at: string | null
  db_doc_id?: number
  is_internet: boolean
  validationState?: null | 'good' | 'bad'
}

export interface ToolCallMetadata {
  tool_name: string
  tool_args: Record<string, any>
  tool_result?: Record<string, any>
}

type CitationMap = { [key: string]: number }

export enum ChatFileType {
  IMAGE = 'image',
  DOCUMENT = 'document',
  PLAIN_TEXT = 'plain_text',
  CSV = 'csv',
}

export interface FileDescriptor {
  id: string
  type: ChatFileType
  name?: string | null

  // FE only
  isUploading?: boolean
}

export interface ToolCallFinalResult {
  tool_name: string
  tool_args: Record<string, any>
  tool_result: Record<string, any>
}

export interface BaseQuestionIdentifier {
  level: number
  level_question_num: number
}

export interface SubQueryDetail {
  query: string
  query_id: number
  doc_ids?: number[] | null
}

export interface SubQuestionDetail extends BaseQuestionIdentifier {
  question: string
  answer: string
  sub_queries?: SubQueryDetail[] | null
  context_docs?: { top_documents: OnyxDocument[] } | null
  is_complete?: boolean
  is_stopped?: boolean
  answer_streaming?: boolean
}

export interface BackendMessage {
  message_id: number
  message_type: string
  parent_message: number | null
  latest_child_message: number | null
  message: string
  rephrased_query: string | null
  context_docs: { top_documents: OnyxDocument[] } | null
  time_sent: string
  overridden_model: string
  alternate_assistant_id: number | null
  chat_session_id: string
  citations: CitationMap | null
  files: FileDescriptor[]
  tool_call: ToolCallFinalResult | null

  sub_questions: SubQuestionDetail[]
  // Keeping existing properties
  comments: any
  parentMessageId: number | null
  refined_answer_improvement: boolean | null
  is_agentic: boolean | null
}

export interface AnswerPiecePacket {
  answer_piece: string
}

export const FlowType = {
  SEARCH: 'search',
  QUESTION_ANSWER: 'question-answer',
}
export type FlowType = (typeof FlowType)[keyof typeof FlowType]
export const SearchType = {
  SEMANTIC: 'semantic',
  KEYWORD: 'keyword',
  AUTOMATIC: 'automatic',
  INTERNET: 'internet',
}
export type SearchType = (typeof SearchType)[keyof typeof SearchType]

export interface DocumentInfoPacket {
  top_documents: OnyxDocument[]
  predicted_flow: FlowType | null
  predicted_search: SearchType | null
  time_cutoff: string | null
  favor_recent: boolean
}

export interface DocumentsResponse {
  top_documents: OnyxDocument[]
  rephrased_query: string | null
  level?: number | null
  level_question_num?: number | null
}

export interface FileChatDisplay {
  file_ids: string[]
}

export interface StreamingError {
  error: string
  stack_trace: string
}

export interface MessageResponseIDInfo {
  user_message_id: number | null
  reserved_assistant_message_id: number
}

export enum StreamStopReason {
  CONTEXT_LENGTH = 'CONTEXT_LENGTH',
  CANCELLED = 'CANCELLED',
}

export interface StreamStopInfo {
  stop_reason: StreamStopReason
  level?: number
  level_question_num?: number
  stream_type?: 'sub_answer' | 'sub_questions' | 'main_answer'
}

export interface ToolResponse {
  id?: string | null
  response?: any
}

export interface ProSearchPacket {
  sub_question?: string
  answer_piece?: string
  sub_query?: string
  tool_response?: ToolResponse
  level: number
  level_question_num: number
}

export interface SubQueryPiece {
  sub_query: string
  level: number
  level_question_num: number
  query_id: number
}

export interface AgentAnswerPiece {
  answer_piece: string
  level: number
  level_question_num: number
  answer_type: 'agent_sub_answer' | 'agent_level_answer'
}

export interface SubQuestionPiece {
  sub_question: string
  level: number
  level_question_num: number
}

export interface ExtendedToolResponse extends ToolResponse {
  level: number
  level_question_num: number
}

export interface RefinedAnswerImprovement {
  refined_answer_improvement: boolean
}

export interface AgentMessageIDInfo {
  level: number
  message_id: number
}

export interface AgenticMessageResponseIDInfo {
  agentic_message_ids: AgentMessageIDInfo[]
}

export interface SubQuestionSearchDoc {
  context_docs: OnyxDocument[]
  level_question_num: number
  level: number
}

export enum ChatSessionSharedStatus {
  Private = 'private',
  Public = 'public',
}

export interface ChatSession {
  id: string
  name: string
  persona_id: number
  time_created: string
  time_updated: string
  shared_status: ChatSessionSharedStatus
  folder_id: number | null
  current_alternate_model: string
  current_temperature_override: number | null
}

export type PacketType =
  | ToolCallMetadata
  | BackendMessage
  | AnswerPiecePacket
  | DocumentInfoPacket
  | DocumentsResponse
  | FileChatDisplay
  | StreamingError
  | MessageResponseIDInfo
  | StreamStopInfo
  | ProSearchPacket
  | SubQueryPiece
  | AgentAnswerPiece
  | SubQuestionPiece
  | ExtendedToolResponse
  | RefinedAnswerImprovement
  | AgenticMessageResponseIDInfo
